import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  memo,
  useCallback,
  useRef,
} from 'react';
import { View, StyleSheet, Text, Image, ActivityIndicator, Animated, FlatList } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { CommonProperties, Color, CommonStyles } from '../../styles';
import { performChargesAction } from '../../services/api/expense.service';
import {
  chargeActions,
  creditCardType,
  EXPENSE_ACTION_TYPES,
  EXPENSE_SUB_TAB_OPTIONS,
} from '../../constants/Enums';
import BaseText from '../BaseText';
import { scale } from 'react-native-size-matters';
import {
  selectExpenseList,
  selectShouldFetchCharges,
  selectSelectedChargesQueue,
  selectExpenseStates,
} from '../../store/selectors/expense.selector';
import {
  setShouldFetchCharges,
  setSelectedChargesQueue,
  initiateExpenseValues,
} from '../../store/actions/expense';
import { filterReportsForNewExpense, isNullOrEmpty } from '../../utils/common.utils';
import Button, { BUTTON_TYPES } from '../Button';
import ExpenseCheckbox from '../ExpenseCheckbox';
import ReportsToggle from './ReportsToggle';
import { useNavigation } from '@react-navigation/native';
import { Modals } from '../../constants/ScreenNames';
import { initializeExpenseState } from '../../utils/expense.utils';
import { ExpenseCategories } from '../../constants/ExpenseCategories';
import { formatCurrency } from '../../utils/currency.utils';
import { selectCompanyCurrencyCulture } from '../../store/selectors/auth.selector';
import moment from 'moment';
import { verticalBase } from '../../styles/CommonProperties';
import { openAlert } from '../../store/actions/alert';

const mastercardImage = require('../../assets/images/creditCard/ca.png');
const visaImage = require('../../assets/images/creditCard/vi.png');

const NULL_DEFAULT_VALUE = null;
const RENDER_PER_BATCH = 10; // Adjust this number based on your performance needs

const TABS = {
  CORPORATE: 'corporate',
  PERSONAL: 'personal',
};
const ChargeItem = memo(({ item, checked, onPressCheckbox, currencyCulture }) => (
  <View style={styles.itemContainer}>
    <View style={styles.mainContent}>
      {/* First row with merchant and amount */}
      <View style={[styles.rowView, styles.alignCenter]}>
        <BaseText style={styles.itemText}>
          {item?.Merchant?.length > 30 ? item?.Merchant?.substring(0, 30) + '...' : item?.Merchant}
        </BaseText>
        <BaseText style={styles.itemText}>
          {formatCurrency(item?.ChargeAmount, item?.Currency, currencyCulture, 2)}
        </BaseText>
      </View>

      {/* Second row with location, date, and card info */}
      <View style={[styles.rowView, styles.alignCenter]}>
        <View style={styles.detailsView}>
          <BaseText style={styles.itemText}>{item?.Location}</BaseText>
          <BaseText style={styles.itemText}>{moment(item.ChargeDate).format('MMM DD')}</BaseText>
        </View>

        <View style={[styles.cardView, styles.alignCenter]}>
          <Image
            source={item?.CardType === creditCardType.MASTERCARD ? mastercardImage : visaImage}
            style={styles.image}
          />
          <BaseText style={styles.itemText}>...{item?.CreditCardLastFourNumbers}</BaseText>
        </View>
      </View>
    </View>

    <View style={styles.checkboxWrapper}>
      <ExpenseCheckbox label="" checked={checked} onPress={onPressCheckbox} />
    </View>
  </View>
));

const ExpenseCharges = forwardRef(({ chargeSheetActions }, ref) => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const shouldFetchCharges = useSelector(selectShouldFetchCharges);
  const [charges, setCharges] = useState(NULL_DEFAULT_VALUE);
  const [renderedCharges, setRenderedCharges] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [noMoreText, setNoMoreText] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { setSelectedCharge, unSetSelectedCharge, forceUnSetSelectedCharge } = chargeSheetActions;
  const [checkedItems, setCheckedItems] = useState({});
  const [activeTab, setActiveTab] = useState(TABS.CORPORATE); // New state for activeTab
  const animation = useRef(new Animated.Value(0)).current;
  const [reportNames, setReportNames] = useState([]);
  const expenseList = useSelector(selectExpenseList);
  const isContinueDisabled = Object.keys(checkedItems).length == 0; // Check if any item is checked
  const selectedChargesQueue = useSelector(selectSelectedChargesQueue);
  const actionType = EXPENSE_ACTION_TYPES.CREATION;
  const subTab = EXPENSE_SUB_TAB_OPTIONS.CHARGES;
  const expenseStates = useSelector(selectExpenseStates)[EXPENSE_ACTION_TYPES.CREATION][subTab];
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);

  useEffect(() => {
    const filteredReportsNames = filterReportsForNewExpense(expenseList);
    setReportNames(filteredReportsNames);
  }, [expenseList]);

  useEffect(() => {
    // 1. Initialize form if needed
    if (isNullOrEmpty(expenseStates)) {
      const formFields = initializeExpenseState(ExpenseCategories);
      dispatch(initiateExpenseValues({ actionType, subTab, formFields }));
    }
  }, []);

  useImperativeHandle(ref, () => ({
    removeChargeFromList(TransactionId) {
      setCharges(charges.filter((charge) => charge.TransactionId !== TransactionId));
    },
  }));

  useEffect(() => {
    Animated.timing(animation, {
      toValue: 1,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [activeTab]);

  useEffect(() => {
    const fetchCharges = async () => {
      setIsFetching(true);
      forceUnSetSelectedCharge();
      const data = await performChargesAction({ ActionCode: chargeActions.GET_CHARGES });
      const rawData = isNullOrEmpty(data) ? [] : JSON.parse(data);
      const newCharges = rawData.filter((item) => Object.keys(item).length !== 0);
      if (newCharges.length === 0) {
        setNoMoreText('No charges found');
      }
      setCharges(newCharges);
      setRenderedCharges(newCharges.slice(0, RENDER_PER_BATCH));
      dispatch(setShouldFetchCharges(false));
      setIsFetching(false);
    };
    if (charges === NULL_DEFAULT_VALUE || shouldFetchCharges) {
      fetchCharges();
    }

    return () => {
      // Code when Charge List is unmounted
      forceUnSetSelectedCharge();
    };
  }, [shouldFetchCharges]);

  const handleLoadMore = () => {
    if (!isFetching && hasMore) {
      setIsFetching(true); // Show loading overlay
      const nextPage = page + 1;
      const nextCharges = charges.slice(
        renderedCharges.length,
        renderedCharges.length + RENDER_PER_BATCH
      );
      setRenderedCharges((prevRenderedCharges) => [...prevRenderedCharges, ...nextCharges]);
      setPage(nextPage);
      setHasMore(nextCharges.length > 0);
      // Simulate network delay
      setTimeout(() => {
        setIsFetching(false); // Hide loading overlay after data is loaded
      }, 500);
    }
  };

  const handleCheckboxPress = (TransactionId) => {
    setCheckedItems((prevCheckedItems) => {
      const updatedCheckedItems = { ...prevCheckedItems };

      if (updatedCheckedItems[TransactionId]) {
        // If the item is already checked, remove it
        delete updatedCheckedItems[TransactionId];
      } else {
        // Otherwise, add it
        updatedCheckedItems[TransactionId] = true;
      }

      console.log(updatedCheckedItems);
      return updatedCheckedItems;
    });
  };

  const onContinue = () => {
    // Get all selected charges as an array
    const selectedCharges = charges.filter((charge) => checkedItems[charge.TransactionId]);
    dispatch(setSelectedChargesQueue(selectedCharges));

    if (activeTab === TABS.CORPORATE) {
      if (selectedCharges.length > 0) {
        navigation.navigate(Modals.CreateExpenseModal, {
          selectedCharge: selectedCharges[0],
          fromExistingReport: false,
          subTab: EXPENSE_SUB_TAB_OPTIONS.CHARGES,
          totalCharges: selectedCharges.length,
          currentChargeIndex: 0,
        });
      }
    } else {
      dispatch(
        openAlert({
          title: 'Personal Charge',
          message: `Please confirm that ${
            selectedCharges.length > 1 ? 'these are personal charges' : 'this is a personal charge'
          }.`,
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          onConfirmPressed: async () => {
            const processedIds = selectedCharges.map((charge) => charge.TransactionId);

            const removedCharges = charges.filter(
              (charge) => !processedIds.includes(charge.TransactionId)
            );
            setCharges(removedCharges);
            setRenderedCharges(
              renderedCharges.filter((charge) => !processedIds.includes(charge.TransactionId))
            );
            setCheckedItems({});

            for (const charge of selectedCharges) {
              const { TransactionId, FirstSixLastFourCardNumber } = charge;
              const params = {
                ActionCode: chargeActions.SET_CHARGE_AS_PERSONAL,
                TransactionId,
                FirstSixLastFourCardNumber,
              };

              try {
                await performChargesAction(params);
              } catch (error) {
                console.error('Error processing charge:', error);
              }
            }
          },
        })
      );
    }
  };

  const renderItem = useCallback(
    ({ item }) => (
      <ChargeItem
        item={item}
        checked={checkedItems[item.TransactionId]}
        onPressCheckbox={() => handleCheckboxPress(item.TransactionId)}
        currencyCulture={currencyCulture}
      />
    ),
    [checkedItems]
  );

  return (
    <View style={styles.container}>
      <View style={styles.buttonContainer}>
        <Animated.View style={{ opacity: animation }}>
          <Button
            label="Corporate"
            onPress={() => setActiveTab(TABS.CORPORATE)}
            labelStyle={
              activeTab === TABS.CORPORATE ? styles.activeButtonLabelText : styles.buttonLabelText
            }
            style={activeTab === TABS.CORPORATE ? styles.activeButtonStyle : styles.buttonStyle}
            type={activeTab === TABS.CORPORATE ? BUTTON_TYPES.OUTLINED : BUTTON_TYPES.TEXT}
          />
        </Animated.View>
        <Animated.View style={{ opacity: animation }}>
          <Button
            label="Personal"
            onPress={() => setActiveTab(TABS.PERSONAL)}
            labelStyle={
              activeTab === TABS.PERSONAL ? styles.activeButtonLabelText : styles.buttonLabelText
            }
            style={activeTab === TABS.PERSONAL ? styles.activeButtonStyle : styles.buttonStyle}
            type={activeTab === TABS.PERSONAL ? BUTTON_TYPES.OUTLINED : BUTTON_TYPES.TEXT}
          />
        </Animated.View>
      </View>

      <View style={CommonStyles.stepOneExpenseCard}>
        {isFetching && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={Color.primary} />
          </View>
        )}
        {charges?.length > 0 ? (
          <FlatList
            data={renderedCharges}
            showsVerticalScrollIndicator={false}
            keyExtractor={(item) => item.TransactionId}
            renderItem={renderItem}
            renderHiddenItem={() => <></>}
            rightOpenValue={scale(-10)}
            previewRowKey={'0'}
            previewOpenValue={-10}
            previewOpenDelay={2000}
            nestedScrollEnabled={true}
            onRowOpen={(TransactionId) => {
              const selectedCharge = charges.find(
                (charge) => charge.TransactionId === TransactionId
              );
              setSelectedCharge(selectedCharge);
            }}
            onRowClose={(TransactionId) => {
              unSetSelectedCharge(TransactionId);
            }}
            initialNumToRender={RENDER_PER_BATCH}
            maxToRenderPerBatch={RENDER_PER_BATCH}
            windowSize={9} // Adjust this number based on your performance needs
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.4} // Load more items when the list is 40% from the end
          />
        ) : (
          <Text style={styles.centerText}>{noMoreText}</Text>
        )}
      </View>
      <View style={styles.reportToggle}>
        <ReportsToggle
          subTab={EXPENSE_SUB_TAB_OPTIONS.CHARGES}
          isDisabled={reportNames?.length === 0}
        />
      </View>

      <Button
        label="Continue"
        style={[
          styles.submitButton,
          isContinueDisabled && {
            backgroundColor: Color.lightgray,
          },
        ]}
        onPress={onContinue}
        labelStyle={[
          CommonStyles.buttonFont,
          isContinueDisabled && {
            color: Color.disabledText,
          },
        ]}
        disabled={isContinueDisabled}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    backgroundColor: Color.accent,
    borderBottomColor: Color.formDividerColor,
    borderBottomWidth: 1,
    ...CommonProperties.expenseFieldHeight,
  },
  itemText: {
    ...CommonProperties.h4,
  },
  rowView: {
    width: '95%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailsView: {
    width: '60%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cardView: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  image: {
    width: scale(30),
    height: scale(30),
    ...CommonProperties.mr3,
  },
  centerText: {
    fontSize: scale(20),
    textAlign: 'center',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },

  // Corporate Personal Button Styles
  buttonContainer: {
    ...CommonProperties.row,
    justifyContent: 'space-around',
    ...CommonProperties.px3,
    ...CommonProperties.pt3,
    ...CommonProperties.pb5,
  },
  buttonLabelText: {
    ...CommonProperties.custom,
    color: Color.black,
  },
  activeButtonLabelText: {
    ...CommonProperties.custom,
    color: Color.active,
  },
  buttonStyle: {
    ...CommonProperties.borderRadius4,
    width: scale(100),
    backgroundColor: Color.transparent,
    ...CommonStyles.p2,
  },
  activeButtonStyle: {
    ...CommonProperties.borderRadius4,
    width: scale(100),
    borderWidth: 2,
    borderColor: Color.active,
    ...CommonStyles.p2,
  },
  reportToggle: {
    marginTop: verticalBase,
  },
  submitButton: {
    marginTop: verticalBase - 2,
    ...CommonProperties.mb5,
    ...CommonProperties.py2,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
  },
  mainContent: {
    flex: 1,
  },
});

export default ExpenseCharges;
